import React, { useEffect, useState, useCallback } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { CrudSection } from '../components/CrudSection';
import { PageHeader, LoadingTable, ErrorState } from '../components/States';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrency, formatPercent, formatNumber } from '../lib/format';
import { Users, Network, Megaphone, Award, Building2, Repeat, TrendingDown, Lock, RefreshCcw, Target, Info, Search, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const CHART = ['hsl(205,78%,45%)', 'hsl(38,92%,50%)', 'hsl(160,60%,42%)', 'hsl(262,52%,55%)', 'hsl(215,14%,52%)', 'hsl(0,72%,55%)', 'hsl(215,10%,68%)'];

// ---- Helper costi personale (UNICA fonte: lista personale) ----
// Fisso = stipendi + rimborsi · Variabile = premi/bonus (% RAL)
function employeeFixed(e) {
  const persons = e.persons || [];
  if (persons.length) {
    return persons.reduce((s, p) => {
      const fte = Number(p.fte ?? 1); const sal = Number(p.monthly_salary || 0);
      const reimb = Number(p.monthly_reimb || 0);
      return s + (sal + reimb) * 12 * fte;
    }, 0);
  }
  const fte = Number(e.fte ?? 1); const sal = Number(e.monthly_salary || 0);
  const reimb = Number(e.monthly_reimb || 0);
  return (sal + reimb) * 12 * fte;
}
function employeeVariable(e) {
  const persons = e.persons || [];
  if (persons.length) {
    return persons.reduce((s, p) => {
      const fte = Number(p.fte ?? 1); const sal = Number(p.monthly_salary || 0);
      return s + (sal * 12 * Number(p.bonus_pct || 0)) * fte;
    }, 0);
  }
  const fte = Number(e.fte ?? 1); const sal = Number(e.monthly_salary || 0);
  return (sal * 12 * Number(e.bonus_pct || 0)) * fte;
}
const employeeAnnual = (e) => employeeFixed(e) + employeeVariable(e);
// Incidenza diretta: personale NON già conteggiato altrove (rete vendita / costo industriale)
const isDirect = (e) => !e.included_in_agents && !e.included_in_industrial_cost;
const agentFixed = (a) => ((a.monthly_fixed || 0) + (a.monthly_reimb || 0)) * 12;
const agentFixedOnly = (a) => (a.monthly_fixed || 0) * 12;
const agentReimb = (a) => (a.monthly_reimb || 0) * 12;
const agentVariable = (a) => (a.managed_volume || 0) * (a.commission_pct || 0);
const agentAnnual = (a) => agentFixed(a) + agentVariable(a);
// La parte "sales" del personale è generata dalla Rete Vendita: la escludiamo dall'organico interno per non doppiare i dati
const SALES_FUNCTIONS = ['sales_dip', 'sales_ag'];
const isSalesEmp = (e) => SALES_FUNCTIONS.includes(e.function);
const isAgency = (a) => (a.category === 'agenzia_italia' || a.category === 'agenzia_estero');

const FN = { logistica: 'Logistica', produzione: 'Produzione', installatori: 'Installatori', ufficio: 'Ufficio', sales_dip: 'Sales (dip.)', sales_ag: 'Sales (ag.)', aftersales: 'AfterSales' };
const DIV = { btob: 'BtoB', impianti: 'Impianti', entrambe: 'Entrambe' };
const CAT = { dipendente: 'Dipendente', agenzia_italia: 'Agenzia Italia', agenzia_estero: 'Agenzia Estero' };

// Editor inline della "% del venduto della divisione" direttamente in tabella (senza aprire la scheda)
const AgentPctCell = ({ agent, sid, canWrite, onSaved }) => {
  const [val, setVal] = useState(String(Math.round(((agent.managed_volume_pct || 0) * 100) * 100) / 100));
  useEffect(() => { setVal(String(Math.round(((agent.managed_volume_pct || 0) * 100) * 100) / 100)); }, [agent.managed_volume_pct]);
  const save = async () => {
    const pct = Math.max(0, (parseFloat(String(val).replace(',', '.')) || 0) / 100);
    if (Math.abs(pct - (agent.managed_volume_pct || 0)) < 1e-9) return;
    try { await api.updateAgent(sid, agent.id, { ...agent, managed_volume_pct: pct }); onSaved && onSaved(); toast.success('% venduto aggiornata'); }
    catch (e) { toast.error('Errore aggiornamento %'); }
  };
  if (!canWrite) return <span className="tabular-nums">{formatPercent(agent.managed_volume_pct || 0, 2)}</span>;
  return (
    <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-end gap-1">
      <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
        className="h-7 w-[4.5rem] text-right tabular-nums px-2" step="0.1" min="0" data-testid={`agent-pct-${agent.id}`} aria-label="% del venduto della divisione" />
      <span className="text-xs text-muted-foreground">%</span>
    </div>
  );
};

function CostDashboard({ cats, total, fissi, variabili, onSelect }) {
  const pieData = cats.filter((c) => (c.chartValue ?? c.value) > 0).map((c) => ({ name: c.label, value: c.chartValue ?? c.value }));
  const [detail, setDetail] = useState(null);
  const DetailIcon = detail?.icon;
  return (
    <div className="space-y-4" data-testid="cost-dashboard">
      {/* Totale + split Fissi/Variabili */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 lg:col-span-4">
          <Card className="p-4 bg-primary/5 border-primary/20 h-full" data-testid="cost-total">
            <div className="text-xs text-muted-foreground mb-1">Totale costi (annuo)</div>
            <div className="text-2xl font-semibold tabular-nums text-primary">{formatCurrency(total)}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Fonte: pagine Personale, Rete Vendita e liste costi</div>
          </Card>
        </div>
        <div className="col-span-6 lg:col-span-4">
          <Card className="p-4 h-full" data-testid="cost-fissi">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Lock className="h-4 w-4" />Costi Fissi</div>
            <div className="text-xl font-semibold tabular-nums">{formatCurrency(fissi)}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{formatPercent(total ? fissi / total : 0)} del totale</div>
          </Card>
        </div>
        <div className="col-span-6 lg:col-span-4">
          <Card className="p-4 h-full" data-testid="cost-variabili">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><RefreshCcw className="h-4 w-4" />Costi Variabili</div>
            <div className="text-xl font-semibold tabular-nums">{formatCurrency(variabili)}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{formatPercent(total ? variabili / total : 0)} del totale</div>
          </Card>
        </div>
      </div>

      {/* Card per categoria (cliccabili → aprono il tab · lente → dettaglio conti) */}
      <div className="grid grid-cols-12 gap-3">
        {cats.map((c) => (
          <div key={c.key} className="col-span-6 lg:col-span-3">
            <Card className={`p-4 h-full transition-shadow ${c.tab ? 'hover:shadow-md hover:border-primary/40' : ''}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0"><c.icon className="h-4 w-4 shrink-0" /><span className="truncate">{c.label}</span></div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs font-medium text-muted-foreground">{formatPercent(total ? c.value / total : 0)}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setDetail(c); }} className="text-muted-foreground/70 hover:text-primary transition-colors p-0.5 -m-0.5 rounded" title={`Dettaglio conti · ${c.label}`} aria-label={`Dettaglio conti ${c.label}`} data-testid={`cost-detail-${c.key}`}>
                    <Search className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <button type="button" onClick={() => c.tab && onSelect(c.tab)} className={`w-full text-left ${c.tab ? 'cursor-pointer' : 'cursor-default'}`} data-testid={`cost-cat-${c.key}`}>
                <div className="text-lg font-semibold tabular-nums">{formatCurrency(c.value)}</div>
                <div className="mt-1"><Badge variant="outline" className="text-[10px] font-normal">{c.nature}</Badge></div>
              </button>
              {c.note && <p className="mt-1.5 text-[10px] leading-snug text-muted-foreground" data-testid={`cost-note-${c.key}`}>{c.note}</p>}
            </Card>
          </div>
        ))}
      </div>

      {/* Popup dettaglio conti */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-lg" data-testid="cost-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{DetailIcon && <DetailIcon className="h-4 w-4 text-primary" />}Dettaglio conti · {detail?.label}</DialogTitle>
            <DialogDescription>{(detail?.rows?.length || 0)} voci · Totale {formatCurrency(detail?.value || 0)}{detail?.nature ? ` · ${detail.nature}` : ''}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-medium px-3 py-2">Conto</th>
                  <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Costo annuo</th>
                  <th className="text-right font-medium px-3 py-2 whitespace-nowrap">% cat.</th>
                </tr>
              </thead>
              <tbody>
                {(detail?.rows || []).map((r, i) => (
                  <tr key={i} className="border-t" data-testid="cost-detail-row">
                    <td className="px-3 py-2">{r.name || '—'}{r.tag ? <span className="ml-1.5 text-[10px] text-muted-foreground">({r.tag})</span> : null}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium whitespace-nowrap">{formatCurrency(r.value)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground whitespace-nowrap">{formatPercent(detail?.value ? r.value / detail.value : 0)}</td>
                  </tr>
                ))}
                {(!detail?.rows || detail.rows.length === 0) && (
                  <tr><td className="px-3 py-8 text-center text-muted-foreground" colSpan={3}>Nessuna voce in questa categoria</td></tr>
                )}
              </tbody>
              {detail?.rows?.length ? (
                <tfoot>
                  <tr className="border-t bg-muted/30 font-semibold">
                    <td className="px-3 py-2">Totale</td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrency(detail?.value || 0)}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">100%</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
          {detail?.tab && (
            <button type="button" onClick={() => { onSelect(detail.tab); setDetail(null); }} className="text-xs text-primary hover:underline self-start" data-testid="cost-detail-goto">Apri e modifica in tabella →</button>
          )}
        </DialogContent>
      </Dialog>

      <Card className="p-5">
        <div className="mb-2"><h3 className="text-sm font-semibold text-foreground">Incidenza per categoria di costo</h3><p className="text-xs text-muted-foreground mt-0.5">Composizione percentuale dei costi annui totali</p></div>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
              {pieData.map((e, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => formatCurrency(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

function StatMini({ icon: Icon, label, value, sub, highlight }) {
  return (
    <Card className={`p-4 h-full ${highlight ? 'bg-primary/5 border-primary/20' : ''}`} data-testid={`personale-stat-${label}`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Icon className="h-4 w-4" />{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${highlight ? 'text-primary' : ''}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

const ReteVolumeCounter = ({ divRows, sharedVol, totVol, totRev }) => {
  const Row = ({ label, vol, rev, hasRev = true, strong = false }) => {
    const delta = vol - rev;
    const over = hasRev && delta > 0.5;
    const direct = hasRev && delta < -0.5;
    return (
      <div className="flex flex-col gap-1.5 rounded-lg border p-3 md:flex-row md:items-center md:justify-between" data-testid={`vol-row-${label}`}>
        <span className={strong ? 'font-semibold' : 'font-medium'}>{label}</span>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
          <span className="text-muted-foreground">Volume rete: <span className="font-semibold tabular-nums text-foreground">{formatCurrency(vol)}</span></span>
          {hasRev && <span className="text-muted-foreground">Ricavi impostati: <span className="font-medium tabular-nums text-foreground">{formatCurrency(rev)}</span></span>}
          {over && (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--destructive)/0.12)] px-2 py-1 font-medium text-[hsl(var(--destructive))]" data-testid="vol-alert">
              <AlertTriangle className="h-4 w-4" /> Volume oltre i ricavi di {formatCurrency(delta)}
            </span>
          )}
          {direct && (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--muted))] px-2 py-1 text-foreground" data-testid="vol-direct">
              <Info className="h-4 w-4" /> Ricavi da vendite dirette (non a provvigione): <span className="font-semibold ml-1">{formatCurrency(-delta)}</span>
            </span>
          )}
          {hasRev && !over && !direct && (
            <span className="inline-flex items-center gap-1.5 text-[hsl(var(--success))]"><CheckCircle2 className="h-4 w-4" /> Allineato</span>
          )}
        </div>
      </div>
    );
  };
  return (
    <Card className="p-4" data-testid="rete-volume-counter">
      <h3 className="text-sm font-semibold mb-1">Confronto volumi Rete Vendita vs Ricavi (per divisione)</h3>
      <p className="text-xs text-muted-foreground mb-3">Volume gestito dalla rete a confronto con i ricavi impostati. Alert se il volume supera i ricavi; altrimenti viene evidenziata la quota di ricavi da vendite dirette non soggetti a provvigione.</p>
      <div className="space-y-2">
        {divRows.map((r) => <Row key={r.key} label={r.label} vol={r.vol} rev={r.rev} />)}
        {sharedVol > 0 && <Row label="Condivisa (shared)" vol={sharedVol} rev={0} hasRev={false} />}
        <Row label="TOTALE" vol={totVol} rev={totRev} strong />
      </div>
    </Card>
  );
};

export default function Costi() {
  const { active, sid, loading, error, reload } = useScenario();
  const { canWriteList } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [tab, setTab] = useState('personale');

  const loadKpi = useCallback(() => { if (sid) api.getKpi(sid).then(setKpi).catch(() => {}); }, [sid]);
  useEffect(() => { loadKpi(); }, [loadKpi, active]);
  const reloadAll = useCallback(() => { reload(); loadKpi(); }, [reload, loadKpi]);

  const [expRV, setExpRV] = useState(false);
  const exportReteVendita = useCallback(async () => {
    if (!sid) return;
    setExpRV(true);
    try {
      const blob = await api.getScenarioReteVenditaPdf(sid);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ReteVendita_${(active?.name || 'piano').replace(/[^a-zA-Z0-9-_]/g, '_')}_${active?.year || ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF Rete Vendita generato');
    } catch (e) {
      toast.error('Errore nella generazione del PDF');
    } finally {
      setExpRV(false);
    }
  }, [sid, active]);

  const toggleAgentFlag = useCallback(async (a, val) => {
    try {
      await api.updateAgent(sid, a.id, { ...a, export_flag: val });
      reloadAll();
    } catch (e) {
      toast.error('Errore aggiornamento flag export');
    }
  }, [sid, reloadAll]);

  // Toggle "Export" generico per le voci di costo (Item lists) e per l'organico (Employee)
  const toggleItemFlag = useCallback(async (listName, row, val) => {
    try {
      await api.updateItem(sid, listName, row.id, { export_flag: val });
      reloadAll();
    } catch (e) {
      toast.error('Errore aggiornamento flag export');
    }
  }, [sid, reloadAll]);
  const toggleEmployeeFlag = useCallback(async (row, val) => {
    try {
      await api.updateEmployee(sid, row.id, { ...row, export_flag: val });
      reloadAll();
    } catch (e) {
      toast.error('Errore aggiornamento flag export');
    }
  }, [sid, reloadAll]);

  // Export PDF dedicato per singola categoria di costo
  const [expCat, setExpCat] = useState(null);
  const exportCategory = useCallback(async (listName, label) => {
    if (!sid) return;
    setExpCat(listName);
    try {
      const blob = await api.getScenarioCategoryPdf(sid, listName);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}_${(active?.name || 'piano').replace(/[^a-zA-Z0-9-_]/g, '_')}_${active?.year || ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF generato');
    } catch (e) {
      toast.error('Errore nella generazione del PDF');
    } finally {
      setExpCat(null);
    }
  }, [sid, active]);

  if (loading) return (<div><PageHeader title="Costi" /><LoadingTable /></div>);
  if (error || !active) return (<div><PageHeader title="Costi" /><ErrorState onRetry={reload} /></div>);

  const canFixed = canWriteList('fixed_costs');
  const canEmployees = canWriteList('employees');
  const canAgents = canWriteList('agents');
  const canMarketing = canWriteList('marketing_items');

  const employees = active.employees || [];
  const agents = active.agents || [];
  const fixed = active.fixed_costs || [];
  const variable = active.variable_costs || [];
  const depr = active.depreciation_items || [];
  const marketing = active.marketing_items || [];
  const sponsor = active.sponsor_items || [];
  const revenues = kpi?.revenues_total || 0;

  // ---- Base ricavi per divisione: il "venduto" del venditore = % del venduto × ricavi della divisione ----
  const baseInst = kpi?.product_mix_revenue || 0;   // Divisione Installatori (include BtoB)
  const baseImp = kpi?.client_mix_revenue || 0;     // Divisione Impianti
  const agentBaseRev = (a) => {
    const d = a.division || 'shared';
    if (d === 'installatori') return baseInst;
    if (d === 'impianti') return baseImp;
    return baseInst + baseImp; // condivisa: entrambe le divisioni vendita
  };
  const agentVolume = (a) => agentBaseRev(a) * (a.managed_volume_pct || 0);
  const agentVariable = (a) => agentVolume(a) * (a.commission_pct || 0);
  const agentAnnual = (a) => agentFixed(a) + agentVariable(a);

  // ---- Margine medio divisione per il beneficio economico dei venditori ----
  const divMargins = active.assumptions?.division_margins || {};
  const opMarginPct = kpi?.margine_operativo_pct || 0;
  const agentMarginPct = (a) => {
    if (a.division === 'installatori') return divMargins['Installatori'] ?? 0.1546;
    if (a.division === 'impianti') return divMargins['Impianti'] ?? 0.277;
    return opMarginPct; // shared/condivisa: margine operativo medio del piano
  };
  const agentBenefit = (a) => agentVolume(a) * agentMarginPct(a);
  const agentBenefitNet = (a) => agentBenefit(a) - agentAnnual(a);

  // ---- ORGANICO INTERNO: solo personale NON-sales (i sales sono generati dalla Rete Vendita) ----
  const orgEmployees = employees.filter((e) => !isSalesEmp(e));
  const orgFte = orgEmployees.reduce((s, r) => s + (r.fte || 0), 0);
  const tPersonale = orgEmployees.reduce((s, r) => s + employeeAnnual(r), 0);           // costo totale organico interno
  const personaleFisso = orgEmployees.reduce((s, r) => s + employeeFixed(r), 0);        // stipendi + rimborsi
  const personaleVar = orgEmployees.reduce((s, r) => s + employeeVariable(r), 0);       // premi / bonus
  const personaleDiretto = orgEmployees.filter(isDirect).reduce((s, r) => s + employeeAnnual(r), 0); // usato nei risultati
  const personaleEscluso = tPersonale - personaleDiretto;                              // già nel costo industriale

  // ---- RETE VENDITA (sales): dettaglio da agents, diviso Dipendenti vs Agenzie ----
  const agDip = agents.filter((a) => a.category === 'dipendente');
  const agAg = agents.filter(isAgency);
  const reteDipFisso = agDip.reduce((s, r) => s + agentFixed(r), 0);
  const reteDipVar = agDip.reduce((s, r) => s + agentVariable(r), 0);
  const reteAgFisso = agAg.reduce((s, r) => s + agentFixed(r), 0);
  const reteAgVar = agAg.reduce((s, r) => s + agentVariable(r), 0);
  const reteFisso = agents.reduce((s, r) => s + agentFixed(r), 0);
  const reteFissoOnly = agents.reduce((s, r) => s + agentFixedOnly(r), 0);
  const reteReimb = agents.reduce((s, r) => s + agentReimb(r), 0);
  const reteVar = agents.reduce((s, r) => s + agentVariable(r), 0);
  const reteBenefit = agents.reduce((s, r) => s + (r.benefit_disabled ? 0 : agentBenefit(r)), 0);
  const reteBenefitNet = agents.reduce((s, r) => s + (r.benefit_disabled ? 0 : agentBenefitNet(r)), 0);
  // ---- Confronto volumi rete per divisione vs ricavi impostati ----
  const volByDiv = { installatori: 0, impianti: 0, shared: 0 };
  agents.forEach((a) => { if (!a.benefit_disabled) { const d = a.division || 'shared'; volByDiv[d] = (volByDiv[d] || 0) + agentVolume(a); } });
  const netDivRows = [
    { key: 'installatori', label: 'Installatori', vol: volByDiv.installatori || 0, rev: baseInst },
    { key: 'impianti', label: 'Impianti', vol: volByDiv.impianti || 0, rev: baseImp },
  ];
  const sharedVol = volByDiv.shared || 0;
  const totVolNet = (volByDiv.installatori || 0) + (volByDiv.impianti || 0) + sharedVol;
  const reteVenduto = agents.reduce((s, r) => s + (r.benefit_disabled ? 0 : agentVolume(r)), 0);
  const tRete = reteFisso + reteVar;
  const tFissiGen = fixed.reduce((s, r) => s + (r.amount || 0) * 12, 0);
  const tVariabili = variable.reduce((s, r) => s + (r.amount || 0), 0);
  const tMarketing = marketing.reduce((s, r) => s + (r.amount || 0), 0);
  const tSponsor = sponsor.reduce((s, r) => s + (r.amount || 0), 0);
  const tAmm = depr.reduce((s, r) => s + (r.amount || 0), 0);

  const totale = tPersonale + tRete + tFissiGen + tVariabili + tMarketing + tSponsor + tAmm;
  const fissiTot = personaleFisso + reteFisso + tFissiGen + tMarketing + tSponsor + tAmm;
  const variabiliTot = personaleVar + reteVar + tVariabili;

  const byValueDesc = (a, b) => (b.value || 0) - (a.value || 0);
  const rowsPersonale = orgEmployees.map((e) => ({ name: e.name, value: employeeAnnual(e), tag: FN[e.function] || e.function })).sort(byValueDesc);
  const rowsRete = agents.map((a) => ({ name: a.name, value: agentAnnual(a), tag: CAT[a.category] || a.category })).sort(byValueDesc);
  const rowsMktg = marketing.map((m) => ({ name: m.name, value: m.amount || 0, tag: m.category })).sort(byValueDesc);
  const rowsSponsor = sponsor.map((s) => ({ name: s.name, value: s.amount || 0, tag: s.category })).sort(byValueDesc);
  const rowsFissi = fixed.map((f) => ({ name: f.name, value: (f.amount || 0) * 12, tag: f.category })).sort(byValueDesc);
  const rowsVariabili = variable.map((v) => ({ name: v.name, value: v.amount || 0, tag: v.category })).sort(byValueDesc);
  // Provvigioni variabili degli agenti (già incluse in Rete Vendita) — mostrate anche nel dettaglio Costi Variabili
  const rowsAgentiVar = agents.filter((a) => agentVariable(a) > 0).map((a) => ({ name: a.name, value: agentVariable(a), tag: 'Provvigioni agente' }));
  const rowsVariabiliFull = [...rowsAgentiVar, ...rowsVariabili].sort(byValueDesc);
  const rowsAmm = depr.map((d) => ({ name: d.name, value: d.amount || 0, tag: d.category })).sort(byValueDesc);

  const cats = [
    { key: 'personale', label: 'Personale', value: tPersonale, icon: Users, nature: 'Fisso + Variabile', tab: 'personale', rows: rowsPersonale },
    { key: 'rete', label: 'Rete Vendita', value: tRete, icon: Network, nature: 'Fisso + Variabile', tab: 'rete', rows: rowsRete },
    { key: 'marketing', label: 'Marketing', value: tMarketing, icon: Megaphone, nature: 'Fisso', tab: 'marketing', rows: rowsMktg },
    { key: 'sponsor', label: 'Sponsor', value: tSponsor, icon: Award, nature: 'Fisso', tab: 'sponsor', rows: rowsSponsor },
    { key: 'fissi', label: 'Costi Fissi generali', value: tFissiGen, icon: Building2, nature: 'Fisso', tab: 'fixed', rows: rowsFissi },
    { key: 'variabili', label: 'Costi Variabili', value: tVariabili + reteVar, chartValue: tVariabili, icon: Repeat, nature: 'Variabile', tab: 'variable', rows: rowsVariabiliFull, note: 'Include le provvigioni variabili degli agenti (già conteggiate in Rete Vendita).' },
    { key: 'ammortamenti', label: 'Ammortamenti', value: tAmm, icon: TrendingDown, nature: 'Fisso', tab: 'depr', rows: rowsAmm },
  ];

  const incidenceCol = (annualOf) => ({
    key: 'incidenza', header: '% sui ricavi', align: 'right', sortable: false,
    render: (r) => revenues ? <span className={(annualOf(r) / revenues) >= 0.02 ? 'font-medium text-foreground' : 'text-muted-foreground'}>{formatPercent(annualOf(r) / revenues, 2)}</span> : <span className="text-muted-foreground">—</span>,
  });
  const baseFields = (amountLabel) => ([
    { name: 'name', label: 'Descrizione', type: 'text' }, { name: 'category', label: 'Categoria', type: 'text' },
    { name: 'amount', label: amountLabel, type: 'number' }, { name: 'note', label: 'Note', type: 'textarea' },
  ]);

  // Colonna toggle "Export" (include/esclude la voce dall'export PDF di categoria)
  const exportCol = (onToggle, canWrite) => ({
    key: 'export_flag', header: 'Export', sortable: false, render: (r) => (
      <div onClick={(e) => e.stopPropagation()} className="flex items-center">
        <Switch checked={r.export_flag !== false} disabled={!canWrite} onCheckedChange={(v) => onToggle(r, v)} data-testid={`export-flag-${r.id}`} aria-label="Includi nell'export PDF" />
      </div>
    ),
  });
  // Barra con contatore inclusi + pulsante export PDF per categoria
  const ExportBar = ({ listName, rows: exRows, label }) => {
    const included = exRows.filter((r) => r.export_flag !== false).length;
    return (
      <div className="flex items-center justify-end gap-3">
        <span className="text-xs text-muted-foreground" data-testid={`${listName}-export-count`}>{included} di {exRows.length} voci incluse nell'export</span>
        <Button variant="outline" size="sm" onClick={() => exportCategory(listName, label)} disabled={expCat === listName} data-testid={`export-${listName}-pdf-btn`}>
          <FileText className="h-4 w-4 mr-1.5" />{expCat === listName ? 'Generazione…' : 'Esporta PDF'}
        </Button>
      </div>
    );
  };

  // Colonne Personale
  const colsEmployees = [
    { key: 'name', header: 'Categoria', render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'division', header: 'Divisione', render: (r) => DIV[r.division] || r.division },
    { key: 'function', header: 'Funzione', render: (r) => FN[r.function] || r.function },
    { key: 'fte', header: 'FTE', align: 'right', render: (r) => formatNumber(r.fte, 1) },
    { key: 'fisso', header: 'Fisso/anno', align: 'right', render: (r) => formatCurrency(employeeFixed(r)), sortValue: (r) => employeeFixed(r) },
    { key: 'variabile', header: 'Variabile/anno', align: 'right', render: (r) => formatCurrency(employeeVariable(r)), sortValue: (r) => employeeVariable(r) },
    { key: 'annual', header: 'Costo annuo', align: 'right', render: (r) => <span className="font-semibold">{formatCurrency(employeeAnnual(r))}</span>, sortValue: (r) => employeeAnnual(r) },
    { key: 'alloc', header: 'Allocazione', sortable: false, render: (r) => (
      <div className="flex gap-1 flex-wrap">
        {r.included_in_industrial_cost && <Badge variant="outline" className="text-[10px]">in Costo Ind.</Badge>}
        {isDirect(r) && <Badge variant="secondary" className="text-[10px]">incidenza diretta</Badge>}
      </div>
    ) },
    incidenceCol((r) => employeeAnnual(r)),
  ];
  const fieldsEmployees = [
    { name: 'name', label: 'Categoria / Reparto', type: 'text' },
    { name: 'division', label: 'Divisione', type: 'select', options: [{ value: 'btob', label: 'BtoB' }, { value: 'impianti', label: 'Impianti' }, { value: 'entrambe', label: 'Entrambe' }] },
    { name: 'function', label: 'Funzione', type: 'select', options: Object.entries(FN).filter(([v]) => !SALES_FUNCTIONS.includes(v)).map(([value, label]) => ({ value, label })) },
    { name: 'role', label: 'Ruolo', type: 'text' },
    { name: 'fte', label: 'FTE (unità equivalenti)', type: 'number', step: '0.1' },
    { name: 'monthly_salary', label: 'Costo mensile per FTE (€)', type: 'number' },
    { name: 'monthly_reimb', label: 'Rimborsi mensili per FTE (€)', type: 'number' },
    { name: 'bonus_pct', label: 'Premio annuo (% RAL)', type: 'percent' },
    { name: 'included_in_industrial_cost', label: 'Già contato nel costo industriale', type: 'checkbox' },
    { name: 'note', label: 'Note', type: 'textarea' },
  ];

  // Colonne Rete Vendita (fisso + provvigioni)
  const colsAgents = [
    { key: 'export_flag', header: 'Export', sortable: false, render: (r) => (
      <div onClick={(e) => e.stopPropagation()} className="flex items-center">
        <Switch checked={r.export_flag !== false} disabled={!canAgents} onCheckedChange={(v) => toggleAgentFlag(r, v)} data-testid={`agent-flag-${r.id}`} aria-label="Includi nell'export PDF" />
      </div>
    ) },
    { key: 'name', header: 'Nome', render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'category', header: 'Categoria', render: (r) => <Badge variant="secondary">{CAT[r.category] || r.category}</Badge>, sortValue: (r) => r.category },
    { key: 'area', header: 'Area', render: (r) => <span className="text-muted-foreground">{r.territory || '—'}</span>, sortValue: (r) => r.territory || '' },
    { key: 'fixed', header: 'Fisso/anno', align: 'right', render: (r) => formatCurrency(agentFixedOnly(r)), sortValue: (r) => agentFixedOnly(r) },
    { key: 'reimb', header: 'Rimborso/anno', align: 'right', render: (r) => formatCurrency(agentReimb(r)), sortValue: (r) => agentReimb(r) },
    { key: 'volume_pct', header: '% venduto div.', align: 'right', sortable: false, render: (r) => <AgentPctCell agent={r} sid={sid} canWrite={canAgents} onSaved={reloadAll} /> },
    { key: 'venduto', header: 'Venduto/anno', align: 'right', render: (r) => r.benefit_disabled ? <span className="text-muted-foreground">—</span> : formatCurrency(agentVolume(r)), sortValue: (r) => r.benefit_disabled ? -1 : agentVolume(r) },
    { key: 'commission_pct', header: 'Provv. %', align: 'right', render: (r) => formatPercent(r.commission_pct, 2) },
    { key: 'variable', header: 'Provvigioni/anno', align: 'right', render: (r) => formatCurrency(agentVariable(r)), sortValue: (r) => agentVariable(r) },
    { key: 'annual', header: 'Costo annuo', align: 'right', render: (r) => <span className="font-semibold">{formatCurrency(agentAnnual(r))}</span>, sortValue: (r) => agentAnnual(r) },
    incidenceCol((r) => agentAnnual(r)),
    { key: 'benefit', header: 'Beneficio economico', align: 'right', render: (r) => r.benefit_disabled ? <span className="text-muted-foreground">—</span> : formatCurrency(agentBenefit(r)), sortValue: (r) => r.benefit_disabled ? -1 : agentBenefit(r) },
    { key: 'benefit_net', header: 'Beneficio netto', align: 'right', render: (r) => r.benefit_disabled ? <span className="text-muted-foreground">—</span> : <span className={`font-medium ${agentBenefitNet(r) < 0 ? 'text-[hsl(var(--destructive))]' : 'text-[hsl(var(--success))]'}`}>{formatCurrency(agentBenefitNet(r))}</span>, sortValue: (r) => r.benefit_disabled ? -1 : agentBenefitNet(r) },
  ];
  const fieldsAgents = [
    { name: 'name', label: 'Nome e cognome', type: 'text' }, { name: 'role', label: 'Ruolo', type: 'text' }, { name: 'territory', label: 'Area (territorio)', type: 'text' },
    { name: 'category', label: 'Categoria', type: 'select', options: [{ value: 'dipendente', label: 'Dipendente' }, { value: 'agenzia_italia', label: 'Agenzia Italia' }, { value: 'agenzia_estero', label: 'Agenzia Estero' }] },
    { name: 'division', label: 'Divisione', type: 'select', options: [{ value: 'installatori', label: 'Installatori' }, { value: 'impianti', label: 'Impianti' }, { value: 'shared', label: 'Condivisa' }] },
    { name: 'monthly_fixed', label: 'Fisso mensile (€)', type: 'number' }, { name: 'monthly_reimb', label: 'Rimborsi mensili (€)', type: 'number' },
    { name: 'commission_pct', label: 'Provvigione (%)', type: 'percent' }, { name: 'managed_volume_pct', label: '% del venduto della divisione', type: 'percent' },
    { name: 'benefit_disabled', label: 'Escludi dal calcolo del beneficio economico', type: 'checkbox' },
    { name: 'note', label: 'Note', type: 'textarea' },
  ];

  const colsFixed = [
    { key: 'name', header: 'Descrizione', render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'category', header: 'Categoria', render: (r) => r.category || '—' },
    { key: 'amount', header: 'Costo mensile', align: 'right', render: (r) => formatCurrency(r.amount) },
    { key: 'annual', header: 'Costo annuo', align: 'right', render: (r) => formatCurrency((r.amount || 0) * 12), sortValue: (r) => (r.amount || 0) * 12 },
    incidenceCol((r) => (r.amount || 0) * 12),
  ];
  const colsAnnual = [
    { key: 'name', header: 'Descrizione', render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'category', header: 'Categoria', render: (r) => r.category || '—' },
    { key: 'amount', header: 'Costo annuo', align: 'right', render: (r) => formatCurrency(r.amount) },
    incidenceCol((r) => r.amount || 0),
  ];
  const colsMktg = colsAnnual;
  const fieldsMktg = [
    { name: 'name', label: 'Descrizione', type: 'text' }, { name: 'category', label: 'Categoria', type: 'text' },
    { name: 'amount', label: 'Costo annuo (€)', type: 'number' }, { name: 'note', label: 'Note', type: 'textarea' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Costi" description="Dashboard e gestione centralizzata di tutti i costi. I totali sono univoci e calcolati dalle liste di Personale, Rete Vendita e dalle voci di costo (distinzione Fissi/Variabili)." />
      <CostDashboard cats={cats} total={totale} fissi={fissiTot} variabili={variabiliTot} onSelect={setTab} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="personale" data-testid="tab-personale"><Users className="h-4 w-4 mr-1.5" />Personale</TabsTrigger>
          <TabsTrigger value="rete" data-testid="tab-rete"><Network className="h-4 w-4 mr-1.5" />Rete Vendita</TabsTrigger>
          <TabsTrigger value="fixed" data-testid="tab-fixed">Costi Fissi</TabsTrigger>
          <TabsTrigger value="depr" data-testid="tab-depr">Ammortamenti</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing</TabsTrigger>
          <TabsTrigger value="sponsor" data-testid="tab-sponsor">Sponsor</TabsTrigger>
          <TabsTrigger value="variable" data-testid="tab-variable">Costi Variabili</TabsTrigger>
        </TabsList>

        <TabsContent value="personale" className="mt-4 space-y-4">
          {/* Riepilogo organico interno (personale non-sales, editabile) */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 sm:col-span-6 lg:col-span-3"><StatMini icon={Users} label="Totale organico interno" value={formatCurrency(tPersonale)} sub={`${formatNumber(orgFte, 1)} FTE · esibito nei costi`} /></div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-3"><StatMini icon={Target} label="Incidenza diretta" value={formatCurrency(personaleDiretto)} sub={`Usato nei calcoli dei risultati · escl. ${formatCurrency(personaleEscluso)} già nel costo industriale`} highlight /></div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-3"><StatMini icon={Lock} label="Costo fisso" value={formatCurrency(personaleFisso)} sub="Stipendi + rimborsi" /></div>
            <div className="col-span-12 sm:col-span-6 lg:col-span-3"><StatMini icon={RefreshCcw} label="Costo variabile" value={formatCurrency(personaleVar)} sub="Premi / bonus (% RAL)" /></div>
          </div>

          {/* Rete Vendita — sola visualizzazione (fonte: tab Rete Vendita) */}
          <Card className="p-4" data-testid="personale-rete-readonly">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Rete Vendita (sales) — sola visualizzazione</h3>
                <Badge variant="outline" className="text-[10px] font-normal">non editabile</Badge>
              </div>
              <button type="button" onClick={() => setTab('rete')} className="text-xs text-primary hover:underline" data-testid="goto-rete-detail">Apri dettaglio Rete Vendita →</button>
            </div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 lg:col-span-6">
                <div className="rounded-lg border p-3 h-full">
                  <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-foreground">Dipendenti</span><Badge variant="secondary" className="text-[10px]">{agDip.length} figure</Badge></div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-[11px] text-muted-foreground">Fisso/anno</div><div className="text-sm font-semibold tabular-nums">{formatCurrency(reteDipFisso)}</div></div>
                    <div><div className="text-[11px] text-muted-foreground">Variabile/anno</div><div className="text-sm font-semibold tabular-nums">{formatCurrency(reteDipVar)}</div></div>
                    <div><div className="text-[11px] text-muted-foreground">Totale/anno</div><div className="text-sm font-semibold tabular-nums text-primary">{formatCurrency(reteDipFisso + reteDipVar)}</div></div>
                  </div>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-6">
                <div className="rounded-lg border p-3 h-full">
                  <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-foreground">Agenzie</span><Badge variant="secondary" className="text-[10px]">{agAg.length} figure</Badge></div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-[11px] text-muted-foreground">Fisso/anno</div><div className="text-sm font-semibold tabular-nums">{formatCurrency(reteAgFisso)}</div></div>
                    <div><div className="text-[11px] text-muted-foreground">Variabile/anno</div><div className="text-sm font-semibold tabular-nums">{formatCurrency(reteAgVar)}</div></div>
                    <div><div className="text-[11px] text-muted-foreground">Totale/anno</div><div className="text-sm font-semibold tabular-nums text-primary">{formatCurrency(reteAgFisso + reteAgVar)}</div></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm border-t pt-2">
              <span className="text-muted-foreground">Totale Rete Vendita</span>
              <span className="font-semibold tabular-nums">{formatCurrency(tRete)} <span className="text-xs font-normal text-muted-foreground">· organico complessivo {formatCurrency(tPersonale + tRete)}</span></span>
            </div>
          </Card>

          <Card className="p-3 bg-muted/40 flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <p className="text-xs text-muted-foreground">L'<span className="font-medium text-foreground">organico interno</span> ({formatCurrency(tPersonale)}) è editabile qui. La <span className="font-medium text-foreground">parte sales</span> è generata dalla <span className="font-medium text-foreground">Rete Vendita</span> ({formatCurrency(tRete)}) ed è mostrata in sola visualizzazione: il dettaglio si gestisce nel tab Rete Vendita, così i dati non vengono duplicati. Nei calcoli dei risultati (Conto Economico) l'organico interno pesa per la sola <span className="font-medium text-foreground">incidenza diretta</span> ({formatCurrency(personaleDiretto)}).</p>
          </Card>

          <ExportBar listName="employees" rows={orgEmployees} label="Personale" />
          <CrudSection title={`Organico interno · ${formatCurrency(tPersonale)}/anno · ${formatNumber(orgFte, 1)} FTE`} rows={orgEmployees} columns={[exportCol((r, v) => toggleEmployeeFlag(r, v), canEmployees), ...colsEmployees]} fields={fieldsEmployees} canWrite={canEmployees} addLabel="Nuova categoria"
            onAdd={(p) => api.addEmployee(sid, p)} onUpdate={(id, p) => api.updateEmployee(sid, id, p)} onDelete={(id) => api.deleteEmployee(sid, id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
        </TabsContent>

        <TabsContent value="rete" className="mt-4 space-y-3">
          <div className="flex items-center justify-end gap-3">
            <span className="text-xs text-muted-foreground" data-testid="rete-export-count">{agents.filter((a) => a.export_flag !== false).length} di {agents.length} venditori inclusi nell'export</span>
            <Button variant="outline" size="sm" onClick={exportReteVendita} disabled={expRV} data-testid="export-rete-vendita-pdf-btn"><FileText className="h-4 w-4 mr-1.5" />{expRV ? 'Generazione…' : 'Esporta PDF Rete Vendita'}</Button>
          </div>
          <CrudSection title={`Rete Vendita · ${formatCurrency(tRete)}/anno · Fisso ${formatCurrency(reteFissoOnly)} + Rimborsi ${formatCurrency(reteReimb)} + Provvigioni ${formatCurrency(reteVar)}`} rows={agents} columns={colsAgents} fields={fieldsAgents} canWrite={canAgents} addLabel="Nuovo agente"
            totals={{
              export_flag: '',
              name: 'Totale',
              fixed: formatCurrency(reteFissoOnly),
              reimb: formatCurrency(reteReimb),
              venduto: formatCurrency(reteVenduto),
              commission_pct: formatPercent(reteVenduto ? reteVar / reteVenduto : 0, 2),
              variable: formatCurrency(reteVar),
              annual: formatCurrency(tRete),
              incidenza: revenues ? formatPercent(tRete / revenues, 2) : '—',
              benefit: formatCurrency(reteBenefit),
              benefit_net: formatCurrency(reteBenefitNet),
            }}
            onAdd={(p) => api.addAgent(sid, p)} onUpdate={(id, p) => api.updateAgent(sid, id, p)} onDelete={(id) => api.deleteAgent(sid, id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
          <ReteVolumeCounter divRows={netDivRows} sharedVol={sharedVol} totVol={totVolNet} totRev={revenues} />
        </TabsContent>

        <TabsContent value="fixed" className="mt-4 space-y-3">
          <ExportBar listName="fixed_costs" rows={fixed} label="CostiFissi" />
          <CrudSection title={`Costi fissi generali (Fisso) · ${formatCurrency(tFissiGen)}/anno`} rows={fixed} columns={[exportCol((r, v) => toggleItemFlag('fixed_costs', r, v), canFixed), ...colsFixed]} fields={baseFields('Costo mensile (€)')} canWrite={canFixed} addLabel="Nuovo costo fisso"
            onAdd={(p) => api.addItem(sid, 'fixed_costs', p)} onUpdate={(id, p) => api.updateItem(sid, 'fixed_costs', id, p)} onDelete={(id) => api.deleteItem(sid, 'fixed_costs', id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
        </TabsContent>

        <TabsContent value="depr" className="mt-4 space-y-3">
          <ExportBar listName="depreciation_items" rows={depr} label="Ammortamenti" />
          <CrudSection title={`Ammortamenti (Fisso) · ${formatCurrency(tAmm)}/anno`} rows={depr} columns={[exportCol((r, v) => toggleItemFlag('depreciation_items', r, v), canFixed), ...colsAnnual]} fields={baseFields('Ammortamento annuo (€)')} canWrite={canFixed} addLabel="Nuovo ammortamento"
            onAdd={(p) => api.addItem(sid, 'depreciation_items', p)} onUpdate={(id, p) => api.updateItem(sid, 'depreciation_items', id, p)} onDelete={(id) => api.deleteItem(sid, 'depreciation_items', id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
        </TabsContent>

        <TabsContent value="marketing" className="mt-4 space-y-3">
          <ExportBar listName="marketing_items" rows={marketing} label="Marketing" />
          <CrudSection title={`Marketing (Fisso) · ${formatCurrency(tMarketing)}/anno`} rows={marketing} columns={[exportCol((r, v) => toggleItemFlag('marketing_items', r, v), canMarketing), ...colsMktg]} fields={fieldsMktg} canWrite={canMarketing} addLabel="Nuova voce marketing"
            onAdd={(p) => api.addItem(sid, 'marketing_items', p)} onUpdate={(id, p) => api.updateItem(sid, 'marketing_items', id, p)} onDelete={(id) => api.deleteItem(sid, 'marketing_items', id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
        </TabsContent>

        <TabsContent value="sponsor" className="mt-4 space-y-3">
          <ExportBar listName="sponsor_items" rows={sponsor} label="Sponsor" />
          <CrudSection title={`Sponsor (Fisso) · ${formatCurrency(tSponsor)}/anno`} rows={sponsor} columns={[exportCol((r, v) => toggleItemFlag('sponsor_items', r, v), canMarketing), ...colsMktg]} fields={fieldsMktg} canWrite={canMarketing} addLabel="Nuova voce sponsor"
            onAdd={(p) => api.addItem(sid, 'sponsor_items', p)} onUpdate={(id, p) => api.updateItem(sid, 'sponsor_items', id, p)} onDelete={(id) => api.deleteItem(sid, 'sponsor_items', id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
        </TabsContent>

        <TabsContent value="variable" className="mt-4 space-y-4">
          {/* Rete Vendita (provvigioni) — sola visualizzazione, già conteggiate nella categoria Rete Vendita */}
          <Card className="p-4" data-testid="variabili-rete-readonly">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Provvigioni Rete Vendita (variabile) — sola visualizzazione</h3>
                <Badge variant="outline" className="text-[10px] font-normal">non editabile</Badge>
              </div>
              <button type="button" onClick={() => setTab('rete')} className="text-xs text-primary hover:underline" data-testid="variabili-goto-rete">Apri dettaglio Rete Vendita →</button>
            </div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6"><div className="rounded-lg border p-3 h-full text-center"><div className="text-[11px] text-muted-foreground mb-0.5">Dipendenti ({agDip.length})</div><div className="text-sm font-semibold tabular-nums">{formatCurrency(reteDipVar)}</div></div></div>
              <div className="col-span-6"><div className="rounded-lg border p-3 h-full text-center"><div className="text-[11px] text-muted-foreground mb-0.5">Agenzie ({agAg.length})</div><div className="text-sm font-semibold tabular-nums">{formatCurrency(reteAgVar)}</div></div></div>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm border-t pt-2">
              <span className="text-muted-foreground">Totale provvigioni Rete Vendita</span>
              <span className="font-semibold tabular-nums">{formatCurrency(reteVar)}</span>
            </div>
          </Card>

          <Card className="p-3 bg-muted/40 flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <p className="text-xs text-muted-foreground">Le <span className="font-medium text-foreground">provvigioni della Rete Vendita</span> ({formatCurrency(reteVar)}) sono la componente variabile principale e sono <span className="font-medium text-foreground">già conteggiate</span> nella categoria Rete Vendita: qui sono mostrate solo per riepilogo e <span className="font-medium text-foreground">non vengono ricalcolate</span>. Sotto puoi aggiungere eventuali <span className="font-medium text-foreground">altri costi variabili</span> ({formatCurrency(tVariabili)}). Totale costi variabili complessivi: <span className="font-medium text-foreground">{formatCurrency(reteVar + tVariabili)}</span>.</p>
          </Card>

          <ExportBar listName="variable_costs" rows={variable} label="CostiVariabili" />
          <CrudSection title={`Altri costi variabili · ${formatCurrency(tVariabili)}/anno`} rows={variable} columns={[exportCol((r, v) => toggleItemFlag('variable_costs', r, v), canFixed), ...colsAnnual]} fields={baseFields('Costo annuo (€)')} canWrite={canFixed} addLabel="Nuovo costo variabile"
            onAdd={(p) => api.addItem(sid, 'variable_costs', p)} onUpdate={(id, p) => api.updateItem(sid, 'variable_costs', id, p)} onDelete={(id) => api.deleteItem(sid, 'variable_costs', id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.name}"?`} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
